import { chartThemeTokens } from 'design-system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import usePrefersReducedMotion from '@/features/charts/hooks/usePrefersReducedMotion';
import {
  createChartAppearance,
  type AppearanceMode
} from '@/features/charts/theme/chartAppearance';
import { Highcharts } from '@/shared/lib/highcharts';

import { applyFullscreenYAxisLayout, installFullscreenYAxisSync } from './fullScreenChartAxis';
import {
  createFullScreenStockOptions,
  withoutInitialRangeSelection
} from './fullScreenChartOptions';
import {
  applyFullscreenRangeSelectorLayout,
  installFullscreenRangeSelectorSync,
  restoreFullscreenRangeSelection
} from './fullScreenChartRangeSelector';
import {
  createFullScreenLegendSeries,
  getSeriesId,
  preserveRuntimeLinkedSeries
} from './fullScreenChartSeries';
import {
  createFullScreenRolloverTooltip,
  useFullScreenRolloverFormatter
} from './fullScreenChartTooltip';

import type HighchartsType from 'highcharts/highstock';

type UseFullScreenChartParams = {
  chartId: string;
  chartSeries: import('./fullScreenChartTypes').CreateFullScreenStockOptionsParams['chartSeries'];
  lightMode: 'dark' | 'light';
};

const useFullScreenChart = ({ chartId, chartSeries, lightMode }: UseFullScreenChartParams) => {
  const { hasPermission } = usePermission();
  const mode: AppearanceMode = lightMode === 'light' ? 'fullscreen-light' : 'fullscreen-dark';
  const appearance = useMemo(() => createChartAppearance(chartThemeTokens, mode), [mode]);
  const prefersReducedMotion = usePrefersReducedMotion();
  const useSmallWatermark = hasPermission(Permission.UseSmallChartWatermark);
  const createTooltipFormatter = useFullScreenRolloverFormatter(appearance);

  const tooltip = useMemo(
    () => createFullScreenRolloverTooltip(createTooltipFormatter, appearance),
    [appearance, createTooltipFormatter]
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<HighchartsType.Chart | null>(null);
  const chartIdRef = useRef<string | null>(null);
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState<string[]>([]);

  const availableSeriesIds = useMemo(
    () => new Set(chartSeries.map((series, index) => getSeriesId(series, index))),
    [chartSeries]
  );
  const activeHiddenSeriesIds = useMemo(
    () => hiddenSeriesIds.filter(id => availableSeriesIds.has(id)),
    [availableSeriesIds, hiddenSeriesIds]
  );

  const options = useMemo(
    () =>
      createFullScreenStockOptions({
        appearance,
        chartSeries,
        hiddenSeriesIds: activeHiddenSeriesIds,
        prefersReducedMotion,
        tooltip,
        useSmallWatermark
      }),
    [
      activeHiddenSeriesIds,
      appearance,
      chartSeries,
      prefersReducedMotion,
      tooltip,
      useSmallWatermark
    ]
  );

  const legendSeries = useMemo(
    () =>
      createFullScreenLegendSeries({
        appearance,
        chartSeries,
        hiddenSeriesIds: activeHiddenSeriesIds
      }),
    [activeHiddenSeriesIds, appearance, chartSeries]
  );

  useEffect(() => {
    if (!containerRef.current) return;

    if (!chartRef.current || chartIdRef.current !== chartId) {
      chartRef.current?.destroy();
      chartRef.current = Highcharts.stockChart(containerRef.current, options);
      chartIdRef.current = chartId;
    } else {
      const selectedRange = (
        chartRef.current as import('./fullScreenChartTypes').FullscreenRuntimeChart
      ).rangeSelector?.selected;
      const updateOptions = preserveRuntimeLinkedSeries(
        withoutInitialRangeSelection(options),
        chartRef.current
      );
      chartRef.current.update(updateOptions, true, true);
      restoreFullscreenRangeSelection(chartRef.current, selectedRange);
    }

    installFullscreenYAxisSync(chartRef.current, appearance);
    installFullscreenRangeSelectorSync(chartRef.current);
    applyFullscreenYAxisLayout(chartRef.current, appearance, true);
    applyFullscreenRangeSelectorLayout(chartRef.current);
  }, [appearance, chartId, options]);

  useEffect(
    () => () => {
      chartRef.current?.destroy();
      chartRef.current = null;
      chartIdRef.current = null;
    },
    []
  );

  const toggleSeriesVisibility = useCallback((seriesId: string) => {
    setHiddenSeriesIds(prev =>
      prev.includes(seriesId) ? prev.filter(id => id !== seriesId) : [...prev, seriesId]
    );
  }, []);

  return { appearance, containerRef, legendSeries, mode, toggleSeriesVisibility };
};

export default useFullScreenChart;
